// @ts-strict-ignore
import React from 'react'
import { Avatar, Card, CardContent, CardHeader, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { graphql, useFragment } from 'react-relay'
import { ReferenceCard_Reference$key } from '__generated__/ReferenceCard_Reference.graphql'
import Condition from 'components/Condition'
import { REFERENCE_STATUS } from './referenceStatus'
import ReferenceCardRating from './components/ReferenceCardRating'

export interface IReferenceCardProps {
  referenceFragmentRef: ReferenceCard_Reference$key
}

function getAvgRating(overall: number, professional: number, expert: number) {
  let count = 0
  let sum = 0

  // note: individual ratings are expected to go from 1 to 5
  // TODO: some of these ratings are essentially unused in practice, look into it and see what we can clean up
  if (overall > 0) {
    sum += overall
    count++
  }

  if (professional > 0) {
    sum += professional
    count++
  }

  if (expert > 0) {
    sum += expert
    count++
  }

  if (count > 0) {
    return sum / count
  }

  return -1
}

function ReferenceCard(props: IReferenceCardProps) {
  const { referenceFragmentRef } = props
  const theme = useTheme()

  const reference = useFragment(
    graphql`
      fragment ReferenceCard_Reference on Reference {
        name
        comments
        ratingOverall
        ratingProfessional
        ratingExpert
        relation
        otherRelation
      }
    `,
    referenceFragmentRef,
  )

  if (!reference) {
    return null
  }

  const rating = getAvgRating(reference.ratingOverall, reference.ratingProfessional, reference.ratingExpert)

  return (
    <Card>
      <CardHeader
        avatar={(
          <Condition condition={Boolean(reference.name)}>
            <Avatar style={{ background: theme.palette.primary.main, color: theme.palette.primary.contrastText }}>
              {reference.name?.[0]}
            </Avatar>
          </Condition>
        )}
        title={reference.name}
        subheader={reference.relation === 'other' ? reference.otherRelation : REFERENCE_STATUS[reference.relation]}
      />

      <CardContent style={{ paddingBottom: theme.spacing(2), paddingTop: 0 }}>
        <Condition condition={rating !== -1}>
          <ReferenceCardRating rating={rating} />
        </Condition>

        <Condition condition={Boolean(reference.comments)}>
          {reference.comments}
        </Condition>

        <Condition condition={!reference.comments}>
          <Typography variant="subtitle1">
            {reference.name} didn't leave any additional comments.
          </Typography>
        </Condition>
      </CardContent>
    </Card>
  )
}

export default React.memo(ReferenceCard)
